/**
 * Centralized navigation service
 * Handles both static and database-driven navigation
 */

import { UserRole, getUserRoleFromString } from '../permissions/roles';
import { hasAnyModuleAccess, getModuleKeysByRole } from '../permissions/roleModulePermissions';
import { LEFT_MENU_GROUPS, TOP_MENUS, getRequiredModulesForUrl, isPublicUrl } from './menus';
import { OrgType, DEFAULT_ORG_TYPE } from '@/lib/types/org';
import { Logger } from '@/lib/logger';

export interface NavigationResult {
  leftMenuGroups: Array<{
    group: string;
    items: Array<{
      id: string;
      label: string;
      path: string;
      icon?: string;
    }>;
  }>;
  topMenus: Record<string, Array<{
    id: string;
    label: string;
    path: string;
  }>>;
  source: 'static' | 'database';
}

export interface UserPermissions {
  modulekey: string;
  canread: boolean;
  canwrite?: boolean;
  candelete?: boolean;
}

/**
 * Main navigation service class
 * Provides navigation based on user role and permissions
 */
export class NavigationService {
  private static instance: NavigationService;
  
  public static getInstance(): NavigationService {
    if (!NavigationService.instance) {
      NavigationService.instance = new NavigationService();
    }
    return NavigationService.instance;
  }

  /**
   * Get navigation for user role (static mode)
   */
  public getStaticNavigation(
    userRole: UserRole,
    orgType: OrgType = DEFAULT_ORG_TYPE
  ): NavigationResult {
    const requestId = Math.random().toString(36).substring(2, 12);
    
    Logger.info('NavigationService', 'Getting static navigation', {
      userRole,
      orgType,
      requestId
    });

    // Get allowed module keys for this role
    const allowedModules = getModuleKeysByRole(userRole);
    
    // Filter left menu groups
    const leftMenuGroups = LEFT_MENU_GROUPS
      .map(group => ({
        ...group,
        items: group.items.filter(item => {
          // Check org type availability
          const isAvailableForOrg = !item.availableFor || 
            item.availableFor === 'all' || 
            item.availableFor.includes(orgType);
          
          // Check module permissions
          const hasModulePermission = !item.requiredModules || 
            item.requiredModules.length === 0 ||
            item.requiredModules.some(module => allowedModules.includes(module));
          
          return isAvailableForOrg && hasModulePermission;
        }).map(item => ({
          id: item.id,
          label: item.label,
          path: item.path,
          icon: item.icon
        }))
      }))
      .filter(group => group.items.length > 0);

    // Filter top menus based on visible left menu items
    const topMenus: Record<string, Array<{ id: string; label: string; path: string }>> = {};
    
    for (const group of leftMenuGroups) {
      for (const item of group.items) {
        const subMenuItems = TOP_MENUS[item.id] || [];
        
        const filteredSubMenus = subMenuItems.filter(subItem => {
          // Check org type availability
          const isAvailableForOrg = !subItem.availableFor || 
            subItem.availableFor === 'all' || 
            subItem.availableFor.includes(orgType);
          
          // Check module permissions
          const hasModulePermission = !subItem.requiredModules || 
            subItem.requiredModules.length === 0 ||
            subItem.requiredModules.some(module => allowedModules.includes(module));
          
          return isAvailableForOrg && hasModulePermission;
        }).map(subItem => ({
          id: subItem.id,
          label: subItem.label,
          path: subItem.path
        }));
        
        if (filteredSubMenus.length > 0) {
          topMenus[item.id] = filteredSubMenus;
        }
      }
    }

    Logger.info('NavigationService', 'Static navigation generated', {
      userRole,
      orgType,
      leftMenuGroupsCount: leftMenuGroups.length,
      topMenusCount: Object.keys(topMenus).length,
      requestId
    });

    return {
      leftMenuGroups,
      topMenus,
      source: 'static'
    };
  }

  /**
   * Get navigation using database permissions
   */
  public getDatabaseNavigation(
    userPermissions: UserPermissions[],
    orgType: OrgType = DEFAULT_ORG_TYPE
  ): NavigationResult {
    const requestId = Math.random().toString(36).substring(2, 12);
    
    Logger.info('NavigationService', 'Getting database navigation', {
      userPermissionsCount: userPermissions.length,
      orgType,
      requestId
    });

    // Filter left menu groups based on database permissions
    const leftMenuGroups = LEFT_MENU_GROUPS
      .map(group => ({
        ...group,
        items: group.items.filter(item => {
          // Check org type availability
          const isAvailableForOrg = !item.availableFor || 
            item.availableFor === 'all' || 
            item.availableFor.includes(orgType);
          
          // Check module permissions from database
          const hasModulePermission = !item.requiredModules || 
            item.requiredModules.length === 0 ||
            item.requiredModules.some(module => 
              userPermissions.some(perm => perm.modulekey === module && perm.canread)
            );
          
          return isAvailableForOrg && hasModulePermission;
        }).map(item => ({
          id: item.id,
          label: item.label,
          path: item.path,
          icon: item.icon
        }))
      }))
      .filter(group => group.items.length > 0);

    // Filter top menus based on visible left menu items and database permissions
    const topMenus: Record<string, Array<{ id: string; label: string; path: string }>> = {};
    
    for (const group of leftMenuGroups) {
      for (const item of group.items) {
        const subMenuItems = TOP_MENUS[item.id] || [];
        
        const filteredSubMenus = subMenuItems.filter(subItem => {
          // Check org type availability
          const isAvailableForOrg = !subItem.availableFor || 
            subItem.availableFor === 'all' || 
            subItem.availableFor.includes(orgType);
          
          // Check module permissions from database
          const hasModulePermission = !subItem.requiredModules || 
            subItem.requiredModules.length === 0 ||
            subItem.requiredModules.some(module => 
              userPermissions.some(perm => perm.modulekey === module && perm.canread)
            );
          
          return isAvailableForOrg && hasModulePermission;
        }).map(subItem => ({
          id: subItem.id,
          label: subItem.label,
          path: subItem.path
        }));
        
        if (filteredSubMenus.length > 0) {
          topMenus[item.id] = filteredSubMenus;
        }
      }
    }

    Logger.info('NavigationService', 'Database navigation generated', {
      userPermissionsCount: userPermissions.length,
      orgType,
      leftMenuGroupsCount: leftMenuGroups.length,
      topMenusCount: Object.keys(topMenus).length,
      requestId
    });

    return {
      leftMenuGroups,
      topMenus,
      source: 'database'
    };
  }

  /**
   * Check if user has permission to access a URL (static mode)
   */
  public hasStaticUrlPermission(
    userRole: UserRole,
    pathname: string
  ): boolean {
    const requestId = Math.random().toString(36).substring(2, 12);
    
    Logger.info('NavigationService', 'Checking static URL permission', {
      userRole,
      pathname,
      requestId
    });

    // Public URLs are always accessible
    if (isPublicUrl(pathname)) {
      Logger.info('NavigationService', 'URL is public, allowing access', {
        pathname,
        requestId
      });
      return true;
    }

    // Get required modules for the URL
    const requiredModules = getRequiredModulesForUrl(pathname);
    
    // If no modules required, deny access (security-first approach)
    if (!requiredModules || requiredModules.length === 0) {
      Logger.warn('NavigationService', 'No modules defined for URL, denying access', {
        pathname,
        requestId
      });
      return false;
    }

    // Check if user role has access to any of the required modules
    const allowedModules = getModuleKeysByRole(userRole);
    const hasAccess = requiredModules.some(module => allowedModules.includes(module));

    Logger.info('NavigationService', 'Static URL permission check result', {
      userRole,
      pathname,
      requiredModules,
      allowedModules,
      hasAccess,
      requestId
    });

    return hasAccess;
  }

  /**
   * Check if user has permission to access a URL (database mode)
   */
  public hasDatabaseUrlPermission(
    userPermissions: UserPermissions[],
    pathname: string
  ): boolean {
    const requestId = Math.random().toString(36).substring(2, 12);
    
    Logger.info('NavigationService', 'Checking database URL permission', {
      userPermissionsCount: userPermissions.length,
      pathname,
      requestId
    });

    // Public URLs are always accessible
    if (isPublicUrl(pathname)) {
      Logger.info('NavigationService', 'URL is public, allowing access', {
        pathname,
        requestId
      });
      return true;
    }

    // Get required modules for the URL
    const requiredModules = getRequiredModulesForUrl(pathname);
    
    // If no modules required, deny access (security-first approach)
    if (!requiredModules || requiredModules.length === 0) {
      Logger.warn('NavigationService', 'No modules defined for URL, denying access', {
        pathname,
        requestId
      });
      return false;
    }

    // Check if user has permission for any of the required modules
    const hasAccess = requiredModules.some(module => 
      userPermissions.some(perm => perm.modulekey === module && perm.canread)
    );

    Logger.info('NavigationService', 'Database URL permission check result', {
      userPermissionsCount: userPermissions.length,
      pathname,
      requiredModules,
      userModules: userPermissions.map(p => p.modulekey),
      hasAccess,
      requestId
    });

    return hasAccess;
  }

  /**
   * Parse user role from string with fallback
   */
  public parseUserRole(roleString: string | null | undefined, fallback: UserRole = UserRole.VIEWER): UserRole {
    const parsed = getUserRoleFromString(roleString);
    return parsed || fallback;
  }
}

// Export singleton instance
export const navigationService = NavigationService.getInstance();
